import {
  Popover,
  PopoverButton,
  PopoverBackdrop,
  PopoverPanel,
} from '@headlessui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { Button } from './../tailwind/components/Button'
import { Container } from './../tailwind/components/Container'
import { NavItem, NavLinks } from './../tailwind/components/NavLinks'
import React, { useEffect, useMemo } from "react";
import { auth } from "../config/firebase";
import LogoutButton from "./auth/LogoutButton";
import { Link, useLocation } from 'react-router-dom'
import { IoEnterOutline } from "react-icons/io5";
import { MdOutlineAccountCircle } from "react-icons/md";
import { FiChevronDown } from 'react-icons/fi'
import { useAppData } from '../context/AppContext'

function MenuIcon(props: React.ComponentPropsWithoutRef<'svg'>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M5 6h14M5 18h14M5 12h14"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ChevronUpIcon(props: React.ComponentPropsWithoutRef<'svg'>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M17 14l-5-5-5 5"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function MobileNavLink(
  props: Omit<
    React.ComponentPropsWithoutRef<typeof PopoverButton<typeof Link>>,
    'as' | 'className'
  >,
) {
  return (
    <PopoverButton
      as={Link}
      className="block text-base/7 tracking-tight text-gray-700"
      {...props}
    />
  )
}

type AuthButtonsProps = {
  onClick?: () => void;
};

const AuthButtons = ({ onClick }: AuthButtonsProps) =>
  <>
    {!auth.currentUser && <>
      <Button to="/log-in" onClick={onClick}>
        Enter <IoEnterOutline size={18} className="ml-2 self-center" />
      </Button>
    </>}
    {!!auth.currentUser && <>
      <Button to="/profile" variant="outline" onClick={onClick}>
        Profile <MdOutlineAccountCircle size={18} className="ml-2 self-center my-1" />
      </Button>
      <LogoutButton onClick={onClick} />
    </>}
  </>

const navLinksGuest: NavItem[] = [
  ['Banana Stand', '/banana-stand'],
  ['Bananaverse Live', '/bananaverse-live'],
  ['How It Works', '/how-it-works'],
  ['About', '/about'],
  ['FAQ', '/faq'],
  ['Glossary', '/glossary'],
]

const navLinksUser: NavItem[] = [
  ['Banana Stand', '/banana-stand'],
  ['Your Bananas', '/your-bananas'],
  ['Bananaverse Live', '/bananaverse-live'],
  ['FAQ', '/faq'],
]

export const Nav = () => {

  const location = useLocation();
  const { data, setData } = useAppData();

  useEffect(() => {
    // Reload on route change
  }, [location.pathname]);

  const navLinks = useMemo(
    () => (!auth.currentUser ? navLinksGuest : navLinksUser),
    [auth.currentUser]
  );

  const navLinksMore = useMemo(
    () => {
      if (!auth.currentUser) {
        return []
      }
      const routes = [
        ['How It Works', '/how-it-works'],
        ['About', '/about'],
        ['Glossary', '/glossary']
      ];

      if (data.user?.role === "admin") {
        routes.push(['Relay Metrics', '/relay-dashboard']);
        routes.push(['Payment Settings', '/payment-settings']);
      }
      return routes;
    },
    [auth.currentUser, data.user?.role]
  );

  return (
    <header>
      <nav>
        <Container className="relative z-50 flex justify-between py-8">
          <div className="relative z-10 flex items-center gap-16">
            <Link to="/" aria-label="Home" className="flex items-center gap-2">
              <img src="/logos/logo192.png" width="40px" className="h-12" alt="Tuttobanana!" />
              <h1 className="text-base/7 font-semibold text-gray-900">Tuttobanana!</h1>
            </Link>
            <div className="hidden lg:flex lg:gap-10">
              <NavLinks links={navLinks} />
              {navLinksMore.length > 0 && <>
                <div className="relative group">
                  <button className="flex items-center gap-1 relative -mx-3 -my-2 rounded-lg px-3 py-2 text-sm text-gray-700 transition-colors delay-150 hover:text-gray-900 hover:delay-0 cursor-pointer">
                    More
                    <FiChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                  </button>

                  {/* Menu */}
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    {navLinksMore.map(([label, href]) => (
                      <Link
                        to={href}
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        {label}
                    </Link>
                    ))}
                  </div>
                </div>
              </>}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <Popover className="lg:hidden">
              {({ open, close }) => (
                <>
                  <div className="flex items-center gap-6 lg:hidden">
                    {!auth.currentUser && <>
                      <Button to="/log-in" className="lg:hidden">
                        Enter <IoEnterOutline size={18} className="ml-2 self-center" />
                      </Button>
                    </>}
                    <PopoverButton
                      className="relative z-10 -m-2 inline-flex items-center rounded-lg stroke-gray-900 p-2 hover:bg-gray-200/50 hover:stroke-gray-600 focus:not-data-focus:outline-hidden active:stroke-gray-900"
                      aria-label="Toggle site navigation"
                    >
                      {({ open }) =>
                        open ? (
                          <ChevronUpIcon className="h-6 w-6" />
                        ) : (
                          <MenuIcon className="h-6 w-6" />
                        )
                      }
                    </PopoverButton>
                  </div>
                  <AnimatePresence initial={false}>
                    {open && (
                      <>
                        <PopoverBackdrop
                          static
                          as={motion.div}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="fixed inset-0 z-0 bg-gray-300/60 backdrop-blur-sm"
                        />
                        <PopoverPanel
                          static
                          as={motion.div}
                          initial={{ opacity: 0, y: -32 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{
                            opacity: 0,
                            y: -32,
                            transition: { duration: 0.2 },
                          }}
                          className="absolute inset-x-0 top-0 z-0 origin-top rounded-b-2xl bg-gray-50 px-6 pt-32 pb-6 shadow-2xl shadow-gray-900/20"
                        >
                          <div className="space-y-4">
                            {[...navLinks, ...navLinksMore].map(([label, href]) => (
                              <MobileNavLink key={label} to={href}>
                                {label}
                              </MobileNavLink>
                            ))}
                          </div>
                          <div className="mt-8 flex flex-col gap-4">
                            <AuthButtons onClick={close} />
                          </div>
                        </PopoverPanel>
                      </>
                    )}
                  </AnimatePresence>
                </>
              )}
            </Popover>
            <div className="flex items-center gap-6 max-lg:hidden">
              <AuthButtons />
            </div>
          </div>
        </Container>
      </nav>
    </header>
  )
}
